/**
 * First-session settlement onboarding, derived entirely from authoritative
 * homestead geometry. Nothing here stores tutorial checkboxes: rebuilding or
 * reconnecting produces the same next step from pieces, rooms, and residents.
 */
import { PIECE_CATALOG, pieceCatalogCost } from "../homestead/pieces.js";
import { homesteadWalkPath, type HomesteadRoomSolution } from "../homestead/rooms.js";
import type { PlacedPiece } from "../homestead/types.js";
import type { ItemStack } from "../rpg.js";
import { PRODUCTION_RECIPE_BY_ID, type SettlementStateV1 } from "./production.js";

/** V2 economy founders (2026-08-13): the founding friends are new characters
 * teaching the food and Materia loops. Ari Calder and Mara Venn retired from
 * founding; the capital's canonical Mara Venn quest-giver is unrelated. */
export const SETTLEMENT_FOUNDER = {
  id: "resident:founder:fenna-osei",
  name: "Fenna Osei",
  profession: "farmer",
  trait: "steady",
} as const;

export const SETTLEMENT_SECOND_FOUNDER = {
  id: "resident:founder:corin-vale",
  name: "Corin Vale",
  profession: "synthesist",
  trait: "curious",
} as const;

/** Persisted allowance revision. V4 replaces the workbench/mining-rig kit with
 * the Biogenerator plot and Materia Synthesizer; older characters receive the
 * new pieces on join. */
export const SETTLEMENT_FOUNDING_KIT_VERSION = 4;

/** Three nights of Q1 food for the two founding friends — the death-spiral
 * protection from the economy spec §2.2. Granted straight to settlement
 * storage when Ari joins, so the first nights never depend on the player. */
export const SETTLEMENT_FOUNDING_FOOD = { id: "wheat", n: 6, quality: "low" } as const;
export const FOUNDING_BUILD_ALLOWANCE_MULTIPLIER = 2;

/** The recommended one-module cabin. The room solver still accepts any valid
 * player-authored equivalent; these counts exist only for guidance/supplies. */
export const FOUNDING_CABIN_PIECES: readonly { id: string; n: number }[] = [
  { id: "hs-floor", n: 1 },
  { id: "hs-wall", n: 3 },
  { id: "hs-wall-door", n: 1 },
  { id: "hs-roof-flat", n: 1 },
  { id: "hs-bed", n: 1 },
  { id: "hs-lamp", n: 1 },
  { id: "hs-farm-plot", n: 1 },
  { id: "hs-shelf", n: 1 },
  { id: "hs-chest", n: 1 },
] as const;

/** Current catalog cost for two recommended cabins plus one first Refiner
 * commission. Two cabin-equivalents cover a conventional 2×2 shell and leave
 * room for experimentation; founding demolition separately refunds in full.
 * Computed rather than duplicated so balance changes cannot soft-lock a new
 * character. */
export function settlementFoundingSupplies(): ItemStack[] {
  const totals = new Map<string, number>();
  for (const need of FOUNDING_CABIN_PIECES) {
    const t = PIECE_CATALOG.findIndex((entry) => entry.id === need.id);
    for (const stack of t < 0 ? [] : pieceCatalogCost(t) ?? []) {
      totals.set(stack.id, (totals.get(stack.id) ?? 0)
        + stack.n * need.n * FOUNDING_BUILD_ALLOWANCE_MULTIPLIER);
    }
  }
  const synthesizer = PIECE_CATALOG.findIndex((entry) => entry.id === "hs-harvester");
  for (const stack of synthesizer < 0 ? [] : pieceCatalogCost(synthesizer) ?? []) {
    totals.set(stack.id, (totals.get(stack.id) ?? 0) + stack.n);
  }
  // Spec §9: enough raw Ferrite for one introductory colored ingot when the
  // Combiner arrives; until then it feeds the Foundry's Ferriron recipe.
  for (const stack of PRODUCTION_RECIPE_BY_ID.get("refine-ferriron")?.inputs ?? []) {
    totals.set(stack.id, (totals.get(stack.id) ?? 0) + stack.n);
  }
  return [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([id, n]) => ({ id, n }));
}

export interface SettlementFoundingPlacement {
  homePid: number;
  workplacePid: number;
  facilityAnchorPid: number;
}

export interface SettlementSecondFounderPlacement extends SettlementFoundingPlacement {}

/** First deterministic reachable bed/farm-plot pair with a recognized Storage
 * room somewhere in the claim. Shared with bootstrap so the HUD cannot promise
 * readiness for geometry the server would reject. */
export function settlementFoundingPlacement(solution: HomesteadRoomSolution,
  pieces: readonly PlacedPiece[]): SettlementFoundingPlacement | null {
  if (!solution.rooms.some((room) => room.functions.includes("storage"))) return null;
  const pieceByPid = new Map(pieces.map((piece) => [piece.pid, piece]));
  const beds = solution.rooms.flatMap((room) => room.functionSlots.dwelling).sort((a, b) => a - b);
  const farms = solution.rooms.flatMap((room) => {
    const facilityAnchorPid = room.functionAnchors.farm;
    return facilityAnchorPid === undefined ? [] : room.functionSlots.farm
      .map((workplacePid) => ({ workplacePid, facilityAnchorPid }));
  }).sort((a, b) => a.facilityAnchorPid - b.facilityAnchorPid || a.workplacePid - b.workplacePid);
  for (const homePid of beds) for (const farm of farms) {
    const home = pieceByPid.get(homePid);
    const work = pieceByPid.get(farm.workplacePid);
    if (home && work && homesteadWalkPath(solution, home.p, work.p)) {
      return { homePid, workplacePid: farm.workplacePid, facilityAnchorPid: farm.facilityAnchorPid };
    }
  }
  return null;
}

/** Corin uses the first vacant reachable bed and Synthesizer station. */
export function settlementSecondFounderPlacement(solution: HomesteadRoomSolution,
  pieces: readonly PlacedPiece[], state: SettlementStateV1): SettlementSecondFounderPlacement | null {
  const occupiedHomes = new Set(state.residents.flatMap((resident) => resident.homePid === null ? [] : [resident.homePid]));
  const occupiedWork = new Set(state.residents.flatMap((resident) => resident.workplacePid === null ? [] : [resident.workplacePid]));
  const pieceByPid = new Map(pieces.map((piece) => [piece.pid, piece]));
  const beds = solution.rooms.flatMap((room) => room.functionSlots.dwelling)
    .filter((pid) => !occupiedHomes.has(pid)).sort((a, b) => a - b);
  const synthesizers = solution.rooms.flatMap((room) => {
    const facilityAnchorPid = room.functionAnchors.harvester;
    return facilityAnchorPid === undefined ? [] : room.functionSlots.harvester
      .filter((workplacePid) => !occupiedWork.has(workplacePid))
      .map((workplacePid) => ({ workplacePid, facilityAnchorPid }));
  }).sort((a, b) => a.facilityAnchorPid - b.facilityAnchorPid || a.workplacePid - b.workplacePid);
  for (const homePid of beds) for (const synthesizer of synthesizers) {
    const home = pieceByPid.get(homePid);
    const work = pieceByPid.get(synthesizer.workplacePid);
    if (home && work && homesteadWalkPath(solution, home.p, work.p)) return { homePid, ...synthesizer };
  }
  return null;
}

export type SettlementFoundingStep =
  | "seal-room"
  | "furnish-dwelling"
  | "furnish-biogenerator"
  | "add-storage"
  | "connect-home-work"
  | "invite-founder"
  | "furnish-second-dwelling"
  | "furnish-synthesizer"
  | "connect-synthesizer-home"
  | "invite-second-founder"
  | "complete";

export interface SettlementFoundingStatus {
  step: SettlementFoundingStep;
  hasRoom: boolean;
  hasDwelling: boolean;
  hasBiogenerator: boolean;
  hasStorage: boolean;
  hasSynthesizer: boolean;
  ready: boolean;
  recommendedPieceIds: string[];
}

function pieceCounts(pieces: readonly PlacedPiece[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const piece of pieces) {
    const id = PIECE_CATALOG[piece.t]?.id;
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Resumable founding checklist for UI and tests. */
export function settlementFoundingStatus(solution: HomesteadRoomSolution,
  pieces: readonly PlacedPiece[], state: SettlementStateV1 | null = null): SettlementFoundingStatus {
  const hasRoom = solution.rooms.length > 0;
  const hasDwelling = solution.rooms.some((room) => room.functions.includes("dwelling"));
  const hasBiogenerator = solution.rooms.some((room) => room.functionAnchors.farm !== undefined
    && room.functionSlots.farm.length > 0);
  const hasStorage = solution.rooms.some((room) => room.functions.includes("storage"));
  const hasSynthesizer = solution.rooms.some((room) => room.functionAnchors.harvester !== undefined
    && room.functionSlots.harvester.length > 0);
  const placement = settlementFoundingPlacement(solution, pieces);
  const ready = placement !== null;
  const hasFirstFriend = !!state && state.residents.some((resident) => resident.id === SETTLEMENT_FOUNDER.id);
  const hasSecondFriend = !!state && state.residents.some((resident) => resident.id === SETTLEMENT_SECOND_FOUNDER.id);
  const secondPlacement = state && hasFirstFriend ? settlementSecondFounderPlacement(solution, pieces, state) : null;
  const complete = hasFirstFriend && hasSecondFriend;
  const counts = pieceCounts(pieces);
  let step: SettlementFoundingStep;
  let recommendedPieceIds: string[];
  if (complete) {
    step = "complete"; recommendedPieceIds = [];
  } else if (hasFirstFriend) {
    const vacantBed = solution.rooms.flatMap((room) => room.functionSlots.dwelling)
      .some((pid) => !state!.residents.some((resident) => resident.homePid === pid));
    if (!vacantBed) {
      step = "furnish-second-dwelling"; recommendedPieceIds = ["hs-bed", "hs-lamp"];
    } else if (!hasSynthesizer) {
      step = "furnish-synthesizer"; recommendedPieceIds = ["hs-harvester"];
    } else if (!secondPlacement) {
      step = "connect-synthesizer-home"; recommendedPieceIds = ["hs-wall-door", "hs-floor"];
    } else {
      step = "invite-second-founder"; recommendedPieceIds = [];
    }
  } else if (!hasRoom) {
    step = "seal-room";
    if ((counts.get("hs-floor") ?? 0) < 1) recommendedPieceIds = ["hs-floor"];
    else if ((counts.get("hs-wall-door") ?? 0) < 1) recommendedPieceIds = ["hs-wall-door"];
    else if ((counts.get("hs-wall") ?? 0) < 3) recommendedPieceIds = ["hs-wall"];
    else if ((counts.get("hs-roof-flat") ?? 0) < 1) recommendedPieceIds = ["hs-roof-flat"];
    else recommendedPieceIds = ["hs-floor", "hs-wall", "hs-wall-door", "hs-roof-flat"];
  } else if (!hasDwelling) {
    step = "furnish-dwelling";
    recommendedPieceIds = [
      ...((counts.get("hs-bed") ?? 0) < 1 ? ["hs-bed"] : []),
      ...((counts.get("hs-lamp") ?? 0) < 1 && (counts.get("hs-hearth") ?? 0) < 1 ? ["hs-lamp"] : []),
    ];
  } else if (!hasBiogenerator) {
    step = "furnish-biogenerator";
    recommendedPieceIds = [...((counts.get("hs-farm-plot") ?? 0) < 1 ? ["hs-farm-plot"] : [])];
  } else if (!hasStorage) {
    step = "add-storage";
    recommendedPieceIds = [
      ...((counts.get("hs-chest") ?? 0) < 1 ? ["hs-chest"] : []),
      ...((counts.get("hs-shelf") ?? 0) < 1 ? ["hs-shelf"] : []),
    ];
  } else if (!ready) {
    step = "connect-home-work";
    recommendedPieceIds = ["hs-wall-door", "hs-floor"];
  } else {
    step = "invite-founder"; recommendedPieceIds = [];
  }
  return { step, hasRoom, hasDwelling, hasBiogenerator, hasStorage, hasSynthesizer, ready, recommendedPieceIds };
}
